import { writable, derived, get } from 'svelte/store';
import { getData, postJsonData } from '../helpers.js';

// Visibility preference: integrations the user has declined (hidden from
// navbar/dashboard) without changing billing. Stored per-account on the
// backend (user/declined-integrations); localStorage is kept as an offline
// fallback so the preference survives if the endpoint errors.
const DECLINED_KEY = 'declined_integrations';

function loadDeclinedFallback() {
	try {
		const raw = localStorage.getItem(DECLINED_KEY);
		if (raw) {
			const parsed = JSON.parse(raw);
			if (Array.isArray(parsed)) return parsed;
		}
	} catch (e) {
		// corrupted storage; fall back to defaults
	}
	return [];
}

export const declinedIntegrations = writable(loadDeclinedFallback());

declinedIntegrations.subscribe(val => {
	try {
		localStorage.setItem(DECLINED_KEY, JSON.stringify(val));
	} catch (e) {
		// storage unavailable (private mode); state stays in-memory
	}
});

export function toggleDeclined(id) {
	declinedIntegrations.update(list =>
		list.includes(id) ? list.filter(x => x !== id) : [...list, id]
	);
	// Persist per-account; on failure the localStorage fallback still holds
	// the new state for this browser.
	postJsonData('user/declined-integrations', { declined: get(declinedIntegrations) })
		.catch(() => {});
}

// Integrations that can appear as navbar tabs.
// ids must match the catalog ids in src/pages/integrations.svelte
// flag: null = freemium (always entitled); otherwise a PaywallFeature name
// returned by GET user/paywall-features (see eCPR-Scraper/custom_types.py)
export const INTEGRATION_TABS = [
	{ id: 'ecpr',        name: 'CPR Search',         path: './cpr-search',    flag: 'CPR_SEARCH',     monogram: 'CPR',  color: '#1d4ed8' },
	{ id: 'dlse',        name: 'Enforcement Search', path: './wage-report',   flag: null,             monogram: 'DLSE', color: '#047857' },
	{ id: 'pwc100',      name: 'PWC100 Search',      path: './pwc100-search', flag: 'PWC100_SEARCH',  monogram: 'PWC',  color: '#4338ca' },
	{ id: 'private-pdf', name: 'db Private',         path: './db-core',       flag: 'PRIVATE_SEARCH', monogram: 'PDF',  color: '#b45309', enterprise: true },
];

// Backend (Stripe/DOMAIN_PAYWALL) entitlements are the source of truth.
export const entitledFeatures = writable([]);
export const subscriptionType = writable('');
export const featuresLoaded = writable(false);

let fetchStarted = false;

// Fetches entitlements once per page load; safe to call from every consumer.
export function loadFeatures() {
	if (fetchStarted) return;
	fetchStarted = true;
	refreshFeatures();
}

// Re-fetches entitlements (e.g. after a subscription change) so the navbar,
// dashboard card, and integration badges update reactively without a reload.
export function refreshFeatures() {
	const featuresPromise = getData('user/paywall-features')
		.then(response => response.json())
		.then(ret_json => {
			if (ret_json.status === 'success') {
				entitledFeatures.set(ret_json.features);
			}
		});

	const subTypePromise = getData('user/subscription-type')
		.then(response => response.json())
		.then(ret_json => {
			if (ret_json.status === 'success') {
				subscriptionType.set(ret_json.subscription_type);
			}
		});

	// Server-side declined list wins over the localStorage fallback; if the
	// endpoint errors, the fallback value already in the store stands.
	const declinedPromise = getData('user/declined-integrations')
		.then(response => response.json())
		.then(ret_json => {
			if (ret_json.status === 'success' && Array.isArray(ret_json.declined)) {
				declinedIntegrations.set(ret_json.declined);
			}
		});

	Promise.allSettled([featuresPromise, subTypePromise, declinedPromise]).then(() => {
		featuresLoaded.set(true);
	});
}

// Non-domain accounts get {CPR_SEARCH, PWC100_SEARCH} free, and MONTHLY (Core)
// subscribers additionally get CORE_FEATURE_SET (CSV_REPORT, PWC100_REPORT,
// GENERATE_CONTRACTOR_TREE) — see eCPR-Scraper/custom_utils.py
// _resolve_feature_set. Extra features no longer imply a DOMAIN_PAYWALL
// customer, so this heuristic misclassifies non-domain Core subscribers.
function isDomainFeatureSet(feats) {
	return feats.some(f => f !== 'CPR_SEARCH' && f !== 'PWC100_SEARCH');
}

// Backend entitlement rules only (Stripe tier + paywall-features).
export const entitledTabs = derived(
	[entitledFeatures, subscriptionType],
	([feats, subType]) => {
		const entitled = INTEGRATION_TABS.filter(t => t.flag === null || feats.includes(t.flag));
		// Only the MONTHLY (core) subscription unlocks the full default set;
		// PER_FILE (10c/file) and NONE (cancelled) tiers are limited to CPR
		// Search plus freemium — unless the domain grants more.
		//if ((subType === 'PER_FILE' || subType === 'NONE') && !isDomainFeatureSet(feats)) {
		//	return entitled.filter(t => t.flag === null || t.flag === 'CPR_SEARCH');
		//}
		return entitled;
	}
);

// Visible tabs: entitled by the backend AND not locally declined.
// Enterprise tabs cannot be locally declined: activation/deactivation is a
// backend (PADG) config change, so stale declined entries are ignored.
export const activeTabs = derived(
	[entitledTabs, declinedIntegrations],
	([tabs, declined]) => tabs.filter(t => t.enterprise || !declined.includes(t.id))
);
